import { useMemo } from 'react';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  CustomAutoComplete,
  CustomTextField,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
} from '../../../common';
import { useUnits } from '../../../hooks/useUnits';
import { useInventoryArticleSearch } from '../hooks/useInventoryArticleSearch';

const InternalOrderMaterialsSection = ({
  materials = [],
  searchEnabled = true,
  onAddMaterial,
  onUpdateMaterialQuantity,
  onRemoveMaterial,
}) => {
  const { data: units = [] } = useUnits({ enabled: searchEnabled });
  const {
    searchTerm,
    setSearchTerm,
    materialOptions,
    isSearching,
    isPendingDebounce,
    hasSearchError,
    resetSearch,
    minSearchLength,
  } = useInventoryArticleSearch(units, searchEnabled);

  const addedMaterialIds = useMemo(
    () => new Set(materials.map((item) => item.materialId)),
    [materials]
  );

  const availableOptions = useMemo(() => {
    if (isPendingDebounce) return [];
    return materialOptions.filter((option) => !addedMaterialIds.has(option.value));
  }, [materialOptions, addedMaterialIds, isPendingDebounce]);

  const handleMaterialSelect = (_, option) => {
    if (!option?.value) return;
    onAddMaterial(option);
    resetSearch();
  };

  const trimmedSearch = searchTerm.trim();

  const noOptionsText = isPendingDebounce
    ? 'Espere un momento sin escribir para buscar'
    : trimmedSearch.length > 0 && trimmedSearch.length < minSearchLength
      ? `Escriba al menos ${minSearchLength} caracteres para buscar`
      : isSearching
        ? 'Buscando artículo...'
        : hasSearchError
          ? 'Error al buscar el artículo'
          : availableOptions.length === 0 && materials.length > 0
            ? 'El artículo ya fue agregado al pedido'
            : trimmedSearch.length >= minSearchLength
              ? 'No se encontró un artículo con ese criterio'
              : 'Escriba código, código externo o descripción';

  const totalUnits = materials.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0),
    0
  );

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        Materiales solicitados
      </Typography>

      <CustomAutoComplete
        label="Buscar material"
        name="material_picker"
        value=""
        onChange={handleMaterialSelect}
        onInputChange={(_, value) => setSearchTerm(value)}
        inputValue={searchTerm}
        options={availableOptions}
        optionValueKey="value"
        optionLabelKey="label"
        placeholder="Código, código externo o descripción (mín. 3 caracteres)"
        size="medium"
        loading={isSearching}
        noOptionsText={noOptionsText}
        filterOptions={(options) => options}
      />

      <GeneralTableBox sx={{ mt: 2 }}>
        <Table size="small" aria-label="materiales del pedido">
          <TableHead>
            <TableRow>
              <HeadTableCell wmd="15%">Código</HeadTableCell>
              <HeadTableCell wmd="40%">Material</HeadTableCell>
              <HeadTableCell wmd="15%">Unidad</HeadTableCell>
              <HeadTableCell wmd="20%">Cantidad</HeadTableCell>
              <HeadTableCell wmd="10%" sx={{ textAlign: 'center' }}>
                Quitar
              </HeadTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {materials.length === 0 ? (
              <TableRow>
                <RowTableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Agregue materiales buscando por código, código externo o descripción
                  </Typography>
                </RowTableCell>
              </TableRow>
            ) : (
              materials.map((item) => (
                <TableRow key={item.materialId}>
                  <RowTableCell fontmd="14px">{item.code}</RowTableCell>
                  <RowTableCell fontmd="14px">{item.name}</RowTableCell>
                  <RowTableCell fontmd="14px">{item.unit}</RowTableCell>
                  <RowTableCell>
                    <CustomTextField
                      name={`quantity-${item.materialId}`}
                      type="number"
                      value={item.quantity}
                      onChange={(event) =>
                        onUpdateMaterialQuantity(item.materialId, event.target.value)
                      }
                      inputProps={{ min: 1, step: 1 }}
                      size="small"
                      sx={{ maxWidth: 120 }}
                    />
                  </RowTableCell>
                  <RowTableCell sx={{ textAlign: 'center' }}>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label="Quitar material"
                      onClick={() => onRemoveMaterial(item.materialId)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </RowTableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </GeneralTableBox>

      {materials.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, px: 0.5 }}>
          {materials.length} material{materials.length !== 1 ? 'es' : ''} · {totalUnits}{' '}
          unidad{totalUnits !== 1 ? 'es' : ''} en total
        </Typography>
      )}
    </Box>
  );
};

export default InternalOrderMaterialsSection;
